import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Confetti from '../components/Confetti';
import InfoTooltip from '../components/InfoTooltip';

const API_BASE = '/api';

// Category display config (same as dashboard)
const CATEGORY_CONFIG = {
  database: {
    label: 'DATABASE',
    bgColor: 'bg-green-50',
    headerBg: 'bg-green-600',
    headerText: 'text-white',
    borderColor: 'border-green-200',
    accentColor: 'text-green-700',
    tagBg: 'bg-green-100 text-green-800',
    icon: '\uD83D\uDCCA',
  },
  grassroots: {
    label: 'GRASSROOTS',
    bgColor: 'bg-yellow-50',
    headerBg: 'bg-yellow-500',
    headerText: 'text-white',
    borderColor: 'border-yellow-200',
    accentColor: 'text-yellow-700',
    tagBg: 'bg-yellow-100 text-yellow-800',
    icon: '\uD83C\uDF31',
  },
  strategic_partners: {
    label: 'STRATEGIC PARTNERS',
    bgColor: 'bg-blue-50',
    headerBg: 'bg-blue-700',
    headerText: 'text-white',
    borderColor: 'border-blue-200',
    accentColor: 'text-blue-700',
    tagBg: 'bg-blue-100 text-blue-800',
    icon: '\uD83E\uDD1D',
  },
};

// Helper to format date as YYYY-MM-DD in local timezone
function toLocalDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getWeekDates(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + mondayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    start: toLocalDateString(monday),
    end: toLocalDateString(sunday),
    monday,
    sunday,
  };
}

function formatWeekRange(startStr, endStr) {
  const start = new Date(startStr + 'T12:00:00');
  const end = new Date(endStr + 'T12:00:00');
  const opts = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', opts)} - ${end.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

// Shift a date string by N days
function shiftDate(dateStr, days) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return toLocalDateString(d);
}

// Validate date string
function isValidDate(dateStr) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
  const d = new Date(dateStr + 'T12:00:00');
  return !isNaN(d.getTime()) && toLocalDateString(d) === dateStr;
}

export default function WeeklyPage() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [habits, setHabits] = useState([]);
  const [weeklyLogs, setWeeklyLogs] = useState({});
  const [dailyScores, setDailyScores] = useState([]);
  const [totalWeeklyXp, setTotalWeeklyXp] = useState(0);
  const [dailyXpTotal, setDailyXpTotal] = useState(0);
  const [weeklyHabitXp, setWeeklyHabitXp] = useState(0);
  const [previousWeek, setPreviousWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showPerfectWeekMessage, setShowPerfectWeekMessage] = useState(false);
  const perfectWeekShownRef = useRef(null);

  const todayStr = toLocalDateString(new Date());
  const currentWeekDates = getWeekDates(todayStr);

  // Initialize selected week from URL param
  const weekParam = searchParams.get('weekOf');
  const initialWeekOf = (weekParam && isValidDate(weekParam) && weekParam <= todayStr)
    ? weekParam
    : todayStr;
  const [selectedWeekOf, setSelectedWeekOf] = useState(initialWeekOf);

  const selectedWeek = getWeekDates(selectedWeekOf);
  const isCurrentWeek = selectedWeek.start === currentWeekDates.start;

  // Can't navigate to future weeks
  const canGoForward = selectedWeek.start < currentWeekDates.start;

  // Sync URL with selected week
  useEffect(() => {
    const currentParam = searchParams.get('weekOf');
    if (isCurrentWeek) {
      if (currentParam) {
        setSearchParams({}, { replace: true });
      }
    } else {
      if (currentParam !== selectedWeek.start) {
        setSearchParams({ weekOf: selectedWeek.start }, { replace: true });
      }
    }
  }, [selectedWeek.start, isCurrentWeek, searchParams, setSearchParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [habitsRes, logsRes, scoresRes] = await Promise.all([
        fetch(`${API_BASE}/habits`, { headers }),
        fetch(`${API_BASE}/logs/week?weekOf=${selectedWeek.start}`, { headers }),
        fetch(`${API_BASE}/scores/weekly?weekOf=${selectedWeek.start}`, { headers }),
      ]);

      const habitsData = await habitsRes.json();
      const logsData = await logsRes.json();
      const scoresData = await scoresRes.json();

      // Filter to only weekly habits
      const weeklyHabits = (habitsData.habits || []).filter(h => h.frequency === 'weekly');
      setHabits(weeklyHabits);

      // Index weekly logs by habit_id (find any log for this week for each weekly habit)
      const logsMap = {};
      (logsData.logs || []).forEach(l => {
        if (l.frequency === 'weekly') {
          // Use the most recent log for this habit this week
          if (!logsMap[l.habit_id] || l.log_date > logsMap[l.habit_id].log_date) {
            logsMap[l.habit_id] = l;
          }
        }
      });
      setWeeklyLogs(logsMap);

      setDailyScores(scoresData.scores || []);
      setTotalWeeklyXp(scoresData.totalWeeklyXp || 0);
      setDailyXpTotal(scoresData.dailyXpTotal || 0);
      setWeeklyHabitXp(scoresData.weeklyHabitXp || 0);
      setPreviousWeek(scoresData.previousWeek || null);
    } catch (err) {
      console.error('Error loading weekly view:', err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedWeek.start]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggle = async (habitId) => {
    if (!isCurrentWeek) return; // Safety: don't allow edits for past weeks
    setToggling(habitId);
    try {
      const res = await fetch(`${API_BASE}/logs/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ habit_id: habitId, date: todayStr }),
      });
      const data = await res.json();
      if (res.ok && data.log) {
        setWeeklyLogs(prev => ({ ...prev, [habitId]: { ...data.log, frequency: 'weekly' } }));
        // Refresh scores
        const headers = { Authorization: `Bearer ${token}` };
        const scoresRes = await fetch(`${API_BASE}/scores/weekly?weekOf=${selectedWeek.start}`, { headers });
        const scoresData = await scoresRes.json();
        setTotalWeeklyXp(scoresData.totalWeeklyXp || 0);
        setDailyXpTotal(scoresData.dailyXpTotal || 0);
        setWeeklyHabitXp(scoresData.weeklyHabitXp || 0);
        setPreviousWeek(scoresData.previousWeek || null);
      }
    } catch (err) {
      console.error('Error toggling habit:', err);
    } finally {
      setToggling(null);
    }
  };

  const navigateWeek = (direction) => {
    if (direction === 'prev') {
      setSelectedWeekOf(prev => shiftDate(prev, -7));
    } else if (direction === 'next' && canGoForward) {
      setSelectedWeekOf(prev => shiftDate(prev, 7));
    }
  };

  const goToCurrentWeek = () => {
    setSelectedWeekOf(todayStr);
  };

  // Count completed weekly habits
  const completedCount = habits.filter(h => weeklyLogs[h.id]?.completed).length;
  const totalWeeklyHabits = habits.length;
  const weeklyProgressPercent = totalWeeklyHabits > 0
    ? Math.round((completedCount / totalWeeklyHabits) * 100)
    : 0;

  // Check if this is a perfect week (all daily targets met every day + all weekly habits completed)
  const allWeeklyHabitsComplete = totalWeeklyHabits > 0 && completedCount === totalWeeklyHabits;
  const allDailyTargetsMet = dailyScores.length > 0 && dailyScores.every(score => score.all_targets_met === 1);
  const isPerfectWeek = allWeeklyHabitsComplete && allDailyTargetsMet;

  // Trigger celebration when transitioning to perfect week (not on initial load)
  useEffect(() => {
    if (!loading && isCurrentWeek && !showPerfectWeekMessage) {
      if (isPerfectWeek && perfectWeekShownRef.current !== selectedWeek.start) {
        setShowConfetti(true);
        setShowPerfectWeekMessage(true);
        perfectWeekShownRef.current = selectedWeek.start;
      }
    }
  }, [isPerfectWeek, loading, isCurrentWeek, showPerfectWeekMessage, selectedWeek.start]);

  const handleConfettiComplete = () => {
    setShowConfetti(false);
  };

  // Group weekly habits by category
  const groupedHabits = {};
  habits.forEach(habit => {
    if (!groupedHabits[habit.category]) {
      groupedHabits[habit.category] = [];
    }
    groupedHabits[habit.category].push(habit);
  });

  const categoryOrder = ['database', 'grassroots', 'strategic_partners'];

  // Mini Calendar Component - shows which days had all targets met
  function MiniWeekCalendar({ dailyScores, weekStart, weekEnd }) {
    const days = [];
    const startDate = new Date(weekStart + 'T12:00:00');
    const endDate = new Date(weekEnd + 'T12:00:00');

    // Build score map
    const scoreMap = {};
    (dailyScores || []).forEach(s => {
      scoreMap[s.score_date] = s;
    });

    // Generate all days in the week
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = toLocalDateString(d);
      const score = scoreMap[dateStr];
      const allMet = score ? score.all_targets_met : false;
      const hasData = !!score;
      const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
      const dayNum = d.getDate();

      days.push({ dateStr, dayName, dayNum, allMet, hasData });
    }

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-6" data-testid="mini-week-calendar">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Daily Target Completion</h3>
        <div className="grid grid-cols-7 gap-2">
          {days.map((day, idx) => (
            <div key={idx} className="text-center">
              <div className="text-xs text-gray-400 mb-1">{day.dayName}</div>
              <div
                className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center text-sm font-medium ${
                  !day.hasData
                    ? 'bg-gray-100 text-gray-400'
                    : day.allMet
                      ? 'bg-green-500 text-white'
                      : 'bg-red-100 text-red-600'
                }`}
                title={day.dateStr}
              >
                {day.dayNum}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-4 mt-3 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>All targets met</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-red-100"></div>
            <span>Below target</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-gray-100"></div>
            <span>No activity</span>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="weekly-page">
      {/* Confetti Celebration */}
      <Confetti active={showConfetti} onComplete={handleConfettiComplete} />

      {/* Perfect Week Celebration Message */}
      {showPerfectWeekMessage && (
        <div
          className="mb-6 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400 rounded-lg p-6 text-center shadow-lg animate-pulse"
          data-testid="perfect-week-message"
        >
          <div className="text-4xl mb-2">🎉🏆🎉</div>
          <h2 className="text-2xl font-bold text-white mb-2">PERFECT WEEK!</h2>
          <p className="text-white font-medium">
            Amazing! You hit all your daily targets and completed all weekly habits!
          </p>
          <button
            onClick={() => setShowPerfectWeekMessage(false)}
            className="mt-3 px-4 py-2 bg-white text-orange-500 rounded-lg font-medium hover:bg-orange-50 transition-colors"
            data-testid="dismiss-perfect-week"
          >
            Thanks!
          </button>
        </div>
      )}

      {/* Week Navigation Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateWeek('prev')}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors text-gray-600 dark:text-gray-300"
            data-testid="week-nav-prev"
            aria-label="Previous week"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white" data-testid="weekly-title">Weekly View</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1" data-testid="week-range">
              {isCurrentWeek
                ? `Week of ${formatWeekRange(selectedWeek.start, selectedWeek.end)}`
                : `${formatWeekRange(selectedWeek.start, selectedWeek.end)} (Read-Only)`
              }
            </p>
          </div>

          <button
            onClick={() => navigateWeek('next')}
            disabled={!canGoForward}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors text-gray-600 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="week-nav-next"
            aria-label="Next week"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Return to Current Week button */}
        {!isCurrentWeek && (
          <div className="mt-3 text-center">
            <button
              onClick={goToCurrentWeek}
              className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-800 transition-colors text-sm font-medium"
              data-testid="week-nav-current"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Back to Current Week
            </button>
          </div>
        )}
      </div>

      {/* Past Week Read-Only Banner */}
      {!isCurrentWeek && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-center" data-testid="past-week-banner">
          <p className="text-sm text-amber-700 font-medium">
            {'\uD83D\uDD12'} You are viewing a past week. Habits are read-only.
          </p>
        </div>
      )}

      {/* Weekly Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Weekly XP</div>
          <div className="text-2xl font-bold text-primary" data-testid="weekly-xp">
            {totalWeeklyXp}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Weekly Habits</div>
          <div className="text-2xl font-bold text-primary" data-testid="weekly-habits-completed">
            {completedCount} <span className="text-sm font-normal text-gray-400">/ {totalWeeklyHabits}</span>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm col-span-2 md:col-span-1">
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Daily XP Total</div>
          <div className="text-2xl font-bold text-primary" data-testid="daily-xp-total">
            {dailyXpTotal}
          </div>
        </div>
      </div>

      {/* Weekly Progress Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-6" data-testid="weekly-progress-bar">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Weekly Habits Progress</span>
          <span className="text-sm text-gray-500">{completedCount} / {totalWeeklyHabits} completed ({weeklyProgressPercent}%)</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-primary rounded-full h-3 transition-all duration-500"
            style={{ width: `${Math.min(weeklyProgressPercent, 100)}%` }}
          ></div>
        </div>
      </div>

      {/* Weekly Habits by Category \u2014 logging section */}
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-lg font-bold text-gray-700 dark:text-gray-100">Weekly Habits</h2>
        <InfoTooltip text="These are your weekly Core Forever Engine activities. By the end of each week, mark each one Done once you've completed it to earn XP and keep your streak going." />
      </div>

      {categoryOrder.map(category => {
        const config = CATEGORY_CONFIG[category];
        const categoryHabits = groupedHabits[category];
        if (!categoryHabits || categoryHabits.length === 0) return null;

        return (
          <div
            key={category}
            className={`rounded-lg border ${config.borderColor} ${config.bgColor} mb-4 overflow-hidden shadow-sm`}
            data-testid={`weekly-category-${category}`}
          >
            {/* Category Header */}
            <div className={`${config.headerBg} ${config.headerText} px-4 py-2 flex items-center gap-2`}>
              <span className="text-lg">{config.icon}</span>
              <h3 className="font-bold text-sm tracking-wider">{config.label}</h3>
            </div>

            {/* Habit Cards */}
            <div className="p-3 space-y-3">
              {categoryHabits.map(habit => {
                const log = weeklyLogs[habit.id];
                const isCompleted = log ? !!log.completed : false;
                const xpEarned = isCompleted ? habit.xp_per_unit : 0;
                const isToggling = toggling === habit.id;

                return (
                  <div
                    key={habit.id}
                    className={`bg-white rounded-lg p-4 border ${isCompleted ? 'border-green-300 ring-1 ring-green-200' : 'border-gray-200'} transition-all`}
                    data-testid={`weekly-habit-card-${habit.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {isCompleted && (
                            <span className="text-green-500 text-lg" data-testid={`weekly-habit-complete-${habit.id}`}>{'\u2705'}</span>
                          )}
                          <h4 className="font-medium text-gray-800 text-sm" data-testid={`weekly-habit-name-${habit.id}`}>
                            {habit.name}
                          </h4>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className={`text-xs font-medium ${isCompleted ? 'text-green-600' : 'text-gray-400'}`} data-testid={`weekly-habit-xp-${habit.id}`}>
                            {xpEarned} / {habit.xp_per_unit} XP
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${config.tagBg}`}>
                            {config.label}
                          </span>
                        </div>
                      </div>

                      {/* Toggle Button */}
                      <button
                        onClick={() => handleToggle(habit.id)}
                        disabled={!isCurrentWeek || isToggling}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          isCompleted
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : !isCurrentWeek
                              ? 'bg-gray-100 text-gray-400'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        data-testid={`weekly-habit-toggle-${habit.id}`}
                        aria-label={`Toggle ${habit.name}`}
                      >
                        <span className="text-lg">{isCompleted ? '\u2705' : '\u2B1C'}</span>
                        <span>{isCompleted ? 'Done' : 'Mark Done'}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Reference zone divider — separates logging (above) from visual recap (below) */}
      <div className="mt-8 mb-4 border-t border-gray-200 pt-6">
        <h2 className="text-lg font-bold text-gray-700 dark:text-gray-100">Your Week at a Glance</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Below is a visual recap of your week so far.</p>
      </div>

      {/* Week-over-Week Comparison — reference */}
      {previousWeek && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-6" data-testid="week-comparison">
          <div className="text-xs text-gray-500 uppercase font-medium mb-3">Week-over-Week Comparison</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-gray-400 mb-1">This Week</div>
              <div className="text-xl font-bold text-primary" data-testid="current-week-xp">{totalWeeklyXp} <span className="text-sm font-normal text-gray-400">XP</span></div>
            </div>
            <div>
              <div className="text-xs text-gray-400 mb-1">Last Week</div>
              <div className="text-xl font-bold text-gray-500" data-testid="previous-week-xp">{previousWeek.totalWeeklyXp} <span className="text-sm font-normal text-gray-400">XP</span></div>
            </div>
          </div>
          {(() => {
            const diff = totalWeeklyXp - previousWeek.totalWeeklyXp;
            const pct = previousWeek.totalWeeklyXp > 0
              ? Math.round((diff / previousWeek.totalWeeklyXp) * 100)
              : totalWeeklyXp > 0 ? 100 : 0;
            const isUp = diff > 0;
            const isDown = diff < 0;
            const isSame = diff === 0;
            return (
              <div className={`mt-3 flex items-center gap-2 text-sm font-medium ${isUp ? 'text-green-600' : isDown ? 'text-red-500' : 'text-gray-500'}`} data-testid="week-comparison-indicator">
                <span className="text-lg">{isUp ? '⬆️' : isDown ? '⬇️' : '➡️'}</span>
                <span>
                  {isSame
                    ? 'Same as last week'
                    : `${isUp ? '+' : ''}${diff} XP (${isUp ? '+' : ''}${pct}%) ${isUp ? 'improvement' : 'decline'} from last week`
                  }
                </span>
              </div>
            );
          })()}
        </div>
      )}

      {/* Mini Week Calendar — reference */}
      <MiniWeekCalendar dailyScores={dailyScores} weekStart={selectedWeek.start} weekEnd={selectedWeek.end} />

      {/* Daily Score Summary */}
      {dailyScores.length > 0 && (
        <div className="mt-6">
          <h2 className="text-lg font-bold text-gray-700 dark:text-gray-100 mb-4">Daily XP This Week</h2>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="divide-y divide-gray-100">
              {dailyScores.map(score => {
                const date = new Date(score.score_date + 'T12:00:00');
                const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const pct = score.max_possible_xp > 0 ? Math.round((score.total_xp / score.max_possible_xp) * 100) : 0;

                return (
                  <div key={score.score_date} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${
                        score.all_targets_met ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {score.all_targets_met ? '\u2713' : dayName.charAt(0)}
                      </span>
                      <div>
                        <span className="text-sm font-medium text-gray-800">{dayName}</span>
                        <span className="text-xs text-gray-400 ml-2">{dateLabel}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary rounded-full h-2 transition-all"
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-700 w-16 text-right">
                        {score.total_xp} XP
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
